import {
	DateRangePicker,
	ExportRequestModal,
	ExportSuccessModal,
	FormFieldControl,
	MultiSelect,
} from "@components";
import { getLocalTimeZone, today } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { SearchLg } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { DateValue, Selection } from "react-aria-components";
import { mapInfractionVehicleLookupToSelectItem } from "@/features/infractions/handlers/infraction-create-form.helpers";
import {
	getDriverLookup,
	getInfractionVehiclesLookup,
} from "@/features/infractions/services/infractions.service";
import type { SelectItemType } from "@/shared/components/select/select-shared";
import { useExportEvents } from "@/shared/hooks";
import { REPORT_TYPE } from "@/shared/types/generate-report-request";
import {
	buildGenerateReportRequest,
	formatDateValueToApi,
	normalizeExportFilterIds,
	selectionToNumberIds,
	validateExportDateRange,
} from "@/shared/utils";
import { EVENT_TYPE_FILTER_OPTIONS } from "../constants/events";
import type { EventTypeCode } from "../types/events";

type DateRangeValue = {
	start: DateValue;
	end: DateValue;
} | null;

type EventsExportModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function EventsExportModal({ isOpen, onClose }: EventsExportModalProps) {
	const currentDate = today(getLocalTimeZone());
	const yearlyStartDate = currentDate.subtract({ years: 1 });
	const yearlyStartLabel = `${String(yearlyStartDate.day).padStart(2, "0")}/${String(yearlyStartDate.month).padStart(2, "0")}/${yearlyStartDate.year}`;
	const [selectedEventIds, setSelectedEventIds] = useState<EventTypeCode[]>([]);
	const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([]);
	const [selectedVehicleIds, setSelectedVehicleIds] = useState<number[]>([]);
	const [dateRange, setDateRange] = useState<DateRangeValue>(null);
	const [pullAllSinceStart, setPullAllSinceStart] = useState(false);
	const [eventsError, setEventsError] = useState<string | null>(null);
	const [dateError, setDateError] = useState<string | null>(null);
	const [isSuccessOpen, setIsSuccessOpen] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const { data: vehiclesLookup = [] } = useQuery({
		queryKey: ["events", "vehicles-lookup", "export"],
		queryFn: getInfractionVehiclesLookup,
		enabled: isOpen,
	});

	const { data: driversLookup = [] } = useQuery({
		queryKey: ["events", "drivers-lookup", "export"],
		queryFn: () => getDriverLookup(undefined, 100),
		enabled: isOpen,
	});

	const eventItems = useMemo<SelectItemType[]>(
		() =>
			EVENT_TYPE_FILTER_OPTIONS.map((option) => ({
				id: String(option.id),
				label: option.label,
			})),
		[]
	);

	const driverItems = useMemo<SelectItemType[]>(
		() =>
			driversLookup.map((driver) => ({
				id: String(driver.id),
				label: driver.name ?? `Motorista #${driver.id}`,
			})),
		[driversLookup]
	);

	const vehicleItems = useMemo<SelectItemType[]>(
		() =>
			vehiclesLookup
				.map(mapInfractionVehicleLookupToSelectItem)
				.map((item) => ({
					id: String(item.value),
					label: item.label,
				})),
		[vehiclesLookup]
	);

	const allEventIds = useMemo(
		() => eventItems.map((item) => Number(item.id)),
		[eventItems]
	);
	const allDriverIds = useMemo(
		() => driverItems.map((item) => Number(item.id)),
		[driverItems]
	);
	const allVehicleIds = useMemo(
		() => vehicleItems.map((item) => Number(item.id)),
		[vehicleItems]
	);

	const selectedEventKeys = useMemo(
		() => new Set<string>(selectedEventIds.map(String)),
		[selectedEventIds]
	);
	const selectedDriverKeys = useMemo(
		() => new Set<string>(selectedDriverIds.map(String)),
		[selectedDriverIds]
	);
	const selectedVehicleKeys = useMemo(
		() => new Set<string>(selectedVehicleIds.map(String)),
		[selectedVehicleIds]
	);

	const exportMutation = useExportEvents({
		onSuccess: (response) => {
			onClose();
			setSuccessMessage(response.message);
			setIsSuccessOpen(true);
		},
	});

	useEffect(() => {
		if (!isOpen) {
			setSelectedEventIds([]);
			setSelectedDriverIds([]);
			setSelectedVehicleIds([]);
			setDateRange(null);
			setPullAllSinceStart(false);
			setEventsError(null);
			setDateError(null);
		}
	}, [isOpen]);

	const handleEventsSelectionChange = (keys: Selection) => {
		setSelectedEventIds(
			selectionToNumberIds(keys, allEventIds) as EventTypeCode[]
		);
		setEventsError(null);
	};

	const handleDriversSelectionChange = (keys: Selection) => {
		setSelectedDriverIds(selectionToNumberIds(keys, allDriverIds));
	};

	const handleVehiclesSelectionChange = (keys: Selection) => {
		setSelectedVehicleIds(selectionToNumberIds(keys, allVehicleIds));
	};

	const validateDateRange = (range: DateRangeValue) =>
		range
			? validateExportDateRange(range.start, range.end, {
					enforceMaxOneYear: true,
					maxValue: today(getLocalTimeZone()),
				})
			: null;

	const handleDateRangeChange = (range: DateRangeValue) => {
		const validationError = validateDateRange(range);
		setDateRange(range);
		setDateError(validationError);
	};

	const handlePullAllSinceStartChange = (value: boolean) => {
		const selectedDate = today(getLocalTimeZone());
		setPullAllSinceStart(value);
		setDateRange(
			value
				? { start: selectedDate.subtract({ years: 1 }), end: selectedDate }
				: null
		);
		setDateError(null);
	};

	const handleSubmit = () => {
		let hasError = false;
		const submitDate = today(getLocalTimeZone());
		const exportDateRange = pullAllSinceStart
			? { start: submitDate.subtract({ years: 1 }), end: submitDate }
			: dateRange;

		const validationError =
			(!pullAllSinceStart && dateError) ||
			validateExportDateRange(exportDateRange?.start, exportDateRange?.end, {
				isRequired: true,
				enforceMaxOneYear: true,
				maxValue: submitDate,
			});
		if (validationError) {
			setDateError(validationError);
			hasError = true;
		} else {
			setDateError(null);
		}

		if (hasError) {
			return;
		}

		exportMutation.mutate(
			buildGenerateReportRequest({
				type: REPORT_TYPE.Events,
				initialDate: formatDateValueToApi(exportDateRange?.start),
				finalDate: formatDateValueToApi(exportDateRange?.end),
				pullAllSinceStart: false,
				eventTypeIds: normalizeExportFilterIds(selectedEventIds, allEventIds),
				driverIds: normalizeExportFilterIds(selectedDriverIds, allDriverIds),
				vehicleIds: normalizeExportFilterIds(selectedVehicleIds, allVehicleIds),
			})
		);
	};

	return (
		<>
			<ExportRequestModal
				description="Digite o tipo de evento e a data"
				isOpen={isOpen}
				isSubmitting={exportMutation.isPending}
				onClose={onClose}
				onSubmit={handleSubmit}
				title="Exportar Eventos"
			>
				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={eventsError ?? undefined}
					htmlFor="events-export-event"
					label="Evento"
				>
					<MultiSelect
						aria-label="Selecionar eventos"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum evento encontrado"
						icon={SearchLg}
						isInvalid={!!eventsError}
						items={eventItems}
						onSelectionChange={handleEventsSelectionChange}
						placeholder="Digite ou selecione o(s) nome(s) do(s) evento(s)"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 evento" : `${count} eventos`
						}
						selectedKeys={selectedEventKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					htmlFor="events-export-driver"
					label="Motorista"
				>
					<MultiSelect
						aria-label="Selecionar motoristas"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum motorista encontrado"
						icon={SearchLg}
						items={driverItems}
						onSelectionChange={handleDriversSelectionChange}
						placeholder="Digite ou selecione o(s) motorista(s)"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 motorista" : `${count} motoristas`
						}
						selectedKeys={selectedDriverKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					htmlFor="events-export-vehicle"
					label="Veículo"
				>
					<MultiSelect
						aria-label="Selecionar veículos"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum veículo encontrado"
						icon={SearchLg}
						items={vehicleItems}
						onSelectionChange={handleVehiclesSelectionChange}
						placeholder="Digite ou selecione o(s) veículo(s)"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 veículo" : `${count} veículos`
						}
						selectedKeys={selectedVehicleKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={dateError ?? undefined}
					htmlFor="events-export-date"
					isRequired
					label="Data"
				>
					<DateRangePicker
						aria-label="Período de exportação de eventos"
						buttonClassName="w-full justify-start px-4"
						className="w-full"
						isInvalid={!!dateError}
						maxValue={currentDate}
						onChange={handleDateRangeChange}
						onPullAllSinceStartChange={handlePullAllSinceStartChange}
						pullAllSinceStart={pullAllSinceStart}
						pullAllSinceStartHint=""
						pullAllSinceStartLabel={`Exportar os dados desde a data ${yearlyStartLabel}`}
						pullAllSinceStartTriggerLabel={`Desde ${yearlyStartLabel} até hoje`}
						selectionError={dateError}
						validateSelection={validateDateRange}
						showPullAllSinceStart
						value={dateRange}
					/>
				</FormFieldControl>
			</ExportRequestModal>

			<ExportSuccessModal
				isOpen={isSuccessOpen}
				message={successMessage}
				onClose={() => setIsSuccessOpen(false)}
			/>
		</>
	);
}
